/**
 * The cart.
 *
 * A cart of one-of-one objects behaves differently from a cart of SKUs. There
 * is no quantity control, because `PhysicalItem` quantity is always one
 * (MASTER_SPEC 0.5.3); adding the same item twice is not a quantity, it is a
 * mistake, and the context rejects it. And a row can *disappear* between
 * browsing and checkout, because someone else bought the object — so every row
 * re-reads core rather than trusting what the cart remembered, and a row core
 * no longer publishes says so instead of vanishing quietly.
 *
 * The exact item identity from the Object Dossier travels here unchanged
 * (`ItemIdentity`), which is the packet's requirement that the object the
 * collector inspected stays visible through cart and checkout.
 */

import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { useAnnounce } from "../components/Announcer.js";
import { InternalLink } from "../components/InternalLink.js";
import { ItemIdentity } from "../components/ObjectDossier.js";
import { PublicImage } from "../components/PublicImage.js";
import { Money, StatusChip } from "../components/primitives.js";
import { CheckoutUnavailableBlock, EmptyBlock, ResourceGate } from "../components/states.js";
import { itemPath } from "../lib/coreRoutes.js";
import { parsePublicItem, type PublicItem } from "../lib/publicContract.js";
import { itemHref } from "../lib/routes.js";
import { useResource } from "../lib/useResource.js";

export function CartView(): ReactNode {
  const { cart, config, navigate, handoff } = useStorefront();
  const [outcomes, setOutcomes] = useState<Readonly<Record<string, "checking" | "ready" | "blocked">>>({});
  const report = useCallback((id: string, outcome: "checking" | "ready" | "blocked") => {
    setOutcomes((current) => current[id] === outcome ? current : { ...current, [id]: outcome });
  }, []);
  const ready = cart.filter((id) => outcomes[id] === "ready").length;
  const blocked = cart.filter((id) => outcomes[id] === "blocked");
  const checking = cart.length - ready - blocked.length;
  const canContinue = ready === cart.length;

  if (handoff.quoteID !== null) {
    return (
      <div className="stack cart">
        <h1 className="t-display-l">Your cart</h1>
        <EmptyBlock
          statement="Your checkout is already in progress."
          action={<InternalLink className="btn btn--primary" href="/checkout">Return to checkout</InternalLink>}
        />
        <p className="t-body-m">Review your reserved items and the latest payment status before changing your selection.</p>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="stack">
        <h1 className="t-display-l">Your cart</h1>
        <EmptyBlock
          action={
            <a className="btn btn--secondary" href="/catalog">
              Browse the catalogue
            </a>
          }
          statement="Your cart is empty."
        />
      </div>
    );
  }

  return (
    <div className="stack cart">
      <div className="section-head">
        <h1 className="t-display-l">Your cart</h1>
        <InternalLink href="/catalog">Continue browsing</InternalLink>
      </div>
      <p className="t-body-m">
        {cart.length} exact {cart.length === 1 ? "copy" : "copies"}. Items are reserved when checkout starts.
        You will review the full total before approving payment.
      </p>

      <div className="cart-layout">
      <ul className="cart-list">
        {cart.map((itemID) => (
          <li key={itemID} id={`cart-${itemID}`}>
            <CartRow itemID={itemID} report={report} />
          </li>
        ))}
      </ul>

      <aside className="cart-readiness summary" aria-labelledby="cart-readiness-title">
        <h2 className="t-display-xs" id="cart-readiness-title">Ready for checkout?</h2>
        <p role="status">{ready} {ready === 1 ? "copy" : "copies"} ready{checking > 0 ? `; ${checking} being checked` : ""}{blocked.length > 0 ? `; ${blocked.length} need attention` : ""}.</p>
        {blocked.length === 0 ? null : <a href={`#cart-${blocked[0]}`}>Review the copy that needs attention</a>}
      {config.checkout_enabled ? (
        <div className="actions cart-checkout">
          <button
            className="btn btn--primary"
            disabled={!canContinue}
            onClick={() => {
              navigate("/checkout");
            }}
            type="button"
          >
            Start checkout
          </button>
          <p className="t-body-s t-secondary">
            No account needed. Shipping, tax, and any eligible discounts are confirmed at checkout.
          </p>
        </div>
      ) : (
        <CheckoutUnavailableBlock reason={config.problem ?? "Checkout is switched off."} />
      )}
      <p className="t-body-s t-secondary">Availability and prices are checked again when you reserve.</p>
      </aside>
      </div>
    </div>
  );
}

function CartRow({ itemID, report }: { itemID: string; report: (id: string, outcome: "checking" | "ready" | "blocked") => void }): ReactNode {
  const { transport, removeFromCart, observedItems, rememberItem } = useStorefront();
  const announce = useAnnounce();

  const resource = useResource<PublicItem>(transport, {
    path: itemPath(itemID),
    parse: parsePublicItem,
    requirement: "this item"
  });

  const { state } = resource;
  const gone = state.phase === "failed" && state.outcome.kind === "not-found";
  const outcome = state.phase === "ready" && resource.value?.checkout_eligible === true
    ? "ready" : state.phase === "loading" || state.phase === "retrying" ? "checking" : "blocked";
  useEffect(() => { report(itemID, outcome); }, [itemID, outcome, report]);
  useEffect(() => {
    if (state.phase === "ready" && resource.value !== null) rememberItem(resource.value);
  }, [state.phase, resource.value, rememberItem]);
  const historical = observedItems[itemID];

  useEffect(() => {
    if (gone) announce("One item in your cart is no longer available.");
  }, [gone, announce]);

  if (state.phase === "failed") {
    return (
      <div className="cart-row cart-row--lost" role="alert">
        {historical === undefined ? <p>Saved item reference <span className="t-id">{itemID}</span></p> : <>
          <ItemIdentity title={historical.title} sku={historical.sku} grade={historical.grade} gradingCompany={historical.grading_company} certNumber={historical.cert_number} />
          <p>Previously viewed details. Current identity, price and availability could not be verified.</p>
        </>}
        <p className="t-body-m">
          <StatusChip label={gone ? "No longer available" : "Could not check this copy"} tone="stop" /> {gone ? "This copy is no longer in the catalogue. It may be reserved, sold, or withdrawn." : "The latest details could not be read."}
        </p>
        <p className="t-body-s t-secondary">
          Check again, or remove this item to continue with the rest of your cart.
        </p>
        <div className="state-actions">
          <button className="btn btn--secondary" type="button" onClick={() => { report(itemID, "checking"); resource.reload(); }}>Check this copy again</button>
          <RemoveButton itemID={itemID} label={`Remove ${historical?.title ?? "this item"}`} onRemove={removeFromCart} />
        </div>
      </div>
    );
  }

  return (
    <div className="cart-entry">
      <ResourceGate resource={resource} skeletonCards={1} subject="item" waitingFor="this item">
        {(item, stale) => (
          <div className="cart-row">
            {item.public_derivatives[0] === undefined ? null : (
              <InternalLink className="cart-thumbnail" href={itemHref(item.physical_item_id)}>
                <PublicImage
                  retry={false}
                  alt={`Inspect ${item.title}, SKU ${item.sku}`}
                  className="cart-image"
                  src={item.public_derivatives[0]}
                />
              </InternalLink>
            )}
            <div className="cart-row-identity">
              <ItemIdentity
                certNumber={item.cert_number}
                gradingCompany={item.grading_company}
                grade={item.grade}
                sku={item.sku}
                title={item.title}
              />
              <p className="t-body-s">
                <InternalLink href={itemHref(item.physical_item_id)}>View item details</InternalLink>
              </p>
            </div>
            <div className="cart-row-price">
              <Money minor={item.price_minor} />
              {item.checkout_eligible ? null : (
                <p className="t-body-s" role="alert">
                  <StatusChip label="Not for sale here" tone="stop" /> Remove this item to continue with the rest.
                  {item.availability === "ACTIVE_EBAY" ? (
                    <> <a href={item.public_url} rel="noopener noreferrer" target="_blank">Buy on eBay</a></>
                  ) : null}
                </p>
              )}
              {stale ? <p className="t-body-s">Price and availability need to be checked again.</p> : null}
            </div>
          </div>
        )}
      </ResourceGate>
      <RemoveButton itemID={itemID} label={`Remove ${resource.value?.title ?? "this item"}`} onRemove={removeFromCart} />
    </div>
  );
}

function RemoveButton({
  itemID,
  label,
  onRemove
}: {
  itemID: string;
  label: string;
  onRemove: (id: string) => void;
}): ReactNode {
  const announce = useAnnounce();
  return (
    <button
      className="btn btn--quiet"
      onClick={() => {
        onRemove(itemID);
        announce("Item removed from your cart.");
      }}
      type="button"
    >
      {label}
    </button>
  );
}
